import {
  Environment,
  Network,
  RecordSource,
  Store,
  Observable,
} from 'relay-runtime';
import type {
  RequestParameters,
  Variables,
  GraphQLResponse,
  Observer,
} from 'relay-runtime';
import {
  ApolloLink,
  Observable as ApolloObservable,
} from '@apollo/client/core';
import {
  createSignalIfSupported,
  fallbackHttpConfig,
  parseAndCheckHttpResponse,
  selectHttpOptionsAndBody,
  selectURI,
  serializeFetchParameter,
} from '@apollo/client/link/http';
import {
  extractFiles,
  isExtractableFile,
  type ExtractableFile,
} from 'extract-files';

import { parse } from 'graphql';

import type { DocumentNode } from 'graphql';
import createRelaySubscriptionHandler from 'graphql-ruby-client/subscriptions/createRelaySubscriptionHandler';

const GRAPHQL_ENDPOINT = '/graphql';

interface RelayUploadLinkOptions {
  uri: string;
  headers: Record<string, string>;
}

const appendFormDataFile = (
  formData: FormData,
  fieldName: string,
  file: Blob & { name?: string }
) => {
  formData.append(fieldName, file, file.name);
};

const createRelayUploadLink = ({ uri, headers }: RelayUploadLinkOptions) => {
  const linkConfig = {
    http: { includeExtensions: false },
    options: undefined,
    credentials: undefined,
    headers,
  };

  return new ApolloLink((operation) => {
    const context = operation.getContext();
    const clientAwareness = context.clientAwareness ?? {};
    const { headers: contextHeaders } = context;
    const { name, version } = clientAwareness;

    const contextConfig = {
      http: context.http,
      options: context.fetchOptions,
      credentials: context.credentials,
      headers: {
        ...(name ? { 'apollographql-client-name': name } : {}),
        ...(version ? { 'apollographql-client-version': version } : {}),
        ...(contextHeaders ?? {}),
      },
    };

    const { options, body } = selectHttpOptionsAndBody(
      operation,
      fallbackHttpConfig,
      linkConfig,
      contextConfig
    );
    const { clone, files } = extractFiles(body, '', isExtractableFile);
    const requestUri = selectURI(operation, uri);

    if (files.size > 0) {
      // fetch sets the multipart content type boundary automatically.
      delete (options.headers as Record<string, string>)['content-type'];

      const formData = new FormData();

      formData.append('operations', serializeFetchParameter(clone, 'Payload'));

      const map: Record<string, string[]> = {};
      let index = 0;
      files.forEach((paths: string[]) => {
        map[String(++index)] = paths;
      });
      formData.append('map', JSON.stringify(map));

      index = 0;
      files.forEach((_paths: string[], file: ExtractableFile) => {
        appendFormDataFile(
          formData,
          String(++index),
          file as Blob & { name?: string }
        );
      });

      options.body = formData;
    } else {
      options.body = serializeFetchParameter(clone, 'Payload');
    }

    const { controller } = createSignalIfSupported();

    if (controller instanceof AbortController) {
      options.signal = controller.signal;
    }

    return new ApolloObservable((observer) => {
      let cleaningUp = false;

      fetch(requestUri, options)
        .then((response) => {
          operation.setContext({ response });
          return response;
        })
        .then(parseAndCheckHttpResponse(operation))
        .then((result) => {
          observer.next(result);
          observer.complete();
        })
        .catch((error) => {
          if (!cleaningUp) {
            if (error.result?.errors && error.result?.data) {
              observer.next(error.result);
            }

            observer.error(error);
          }
        });

      return () => {
        cleaningUp = true;

        if (controller instanceof AbortController) controller.abort();
      };
    });
  });
};

const uploadLink = createRelayUploadLink({
  uri: GRAPHQL_ENDPOINT,
  headers: {
    'X-CSRF-Token': AUTH_TOKEN,
    Accept:
      'application/graphql-response+json; charset=utf-8, application/json; charset=utf-8',
    'Content-Type': 'application/json',
  },
});

interface OperationCompat {
  query: DocumentNode;
  variables: Variables;
  extensions: Record<string, unknown>;
  getContext: () => Record<string, unknown> | undefined;
  setContext: (
    newContext: Record<string, unknown>
  ) => Record<string, unknown> | undefined;
}

const fetchFn = (
  operation: RequestParameters,
  variables: Variables
  // cacheConfig: CacheConfig,
  // uploadables?: UploadableMap | null
): Observable<GraphQLResponse> => {
  return Observable.create((observer: Observer<GraphQLResponse>) => {
    {
      const { text: query } = operation;
      const context = {};

      if (query === null) {
        observer.error?.(new Error('Query is null'));
        return;
      }

      if (!navigator.onLine) {
        observer.error?.(new Error('There is no internet access'));
        return;
      }

      if (uploadLink === null) {
        observer.error?.(new Error('uploadLink is null'));
        return;
      }
      // Create an object compatible with the Operation type
      const operationCompat: OperationCompat = {
        query: parse(query),
        variables: variables,
        extensions: {},
        getContext: () => (context ? { ...context } : {}),
        setContext: (newContext: Record<string, unknown>) =>
          context ? { ...context, ...newContext } : { ...newContext },
      };

      // @ts-expect-error - The Relay and Apollo types differ
      uploadLink.request(operationCompat).subscribe({
        next: (response) => {
          if (typeof TEST_CLUSTER_MODE !== 'undefined' && TEST_CLUSTER_MODE) {
            console.log('GraphQL response', JSON.stringify(response));
          }
          if (observer.next) {
            observer.next(response as GraphQLResponse);
          }
        },
        error: (err) => {
          if (err.statusCode === 401) {
            window.location.href = '/login';
          } else {
            if (observer.error) observer.error(err);
          }
        },
        complete: () => {
          if (observer.complete) observer.complete();
        },
      });
    }
  });
};

const actionCable = window.App.cable;

if (actionCable == null) {
  throw new Error('Action Cable consumer is not initialized');
}

const baseSubscriptionFn = createRelaySubscriptionHandler({
  channelName: 'GraphQLChannel',
  cable: actionCable,
});

const loggedSubscriptionFn = (
  operation: RequestParameters,
  variables: Variables
): Observable<GraphQLResponse> => {
  const source = baseSubscriptionFn(
    operation,
    variables
  ) as Observable<GraphQLResponse>;

  return Observable.create<GraphQLResponse>((sink) => {
    const disposable = source.subscribe({
      next: (payload) => {
        if (
          typeof TEST_CLUSTER_MODE !== 'undefined' &&
          /* eslint-disable camelcase */ TEST_CLUSTER_MODE
        ) {
          console.log(
            `Relay subscription: ${operation.name}`,
            JSON.stringify(payload, null, 2)
          );
        }
        sink.next(payload);
      },
      error: (err: Error) => sink.error(err),
      complete: () => sink.complete(),
    });

    return () => {
      disposable.unsubscribe?.();
    };
  });
};

function createRelayEnvironment() {
  return new Environment({
    network: Network.create(fetchFn, loggedSubscriptionFn),
    store: new Store(new RecordSource()),
  });
}

export const RelayEnvironment = createRelayEnvironment();

// Simpler Fetch function that works with Relay But doesn't support file uploads. Try this if there are issues with the above.

// const fetchFn: FetchFunction = async (request, variables) => {
//   const resp = await fetch(HTTP_ENDPOINT, {
//     method: "POST",
//     headers: {
//       Accept:
//         "application/graphql-response+json; charset=utf-8, application/json; charset=utf-8",
//       "Content-Type": "application/json",
//       "X-CSRF-Token": AUTH_TOKEN,
//       // <-- Additional headers like 'Authorization' would go here
//     },
//     body: JSON.stringify({
//       query: request.text, // <-- The GraphQL document composed by Relay
//       variables,
//     }),
//   });

//   return await resp.json();
// };
